import {useCallback, useEffect, useRef, useState} from "react";
import {createSelector} from "@reduxjs/toolkit";
import {AxiosHeaders} from "axios";
import {useDispatch, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import useLiveRef from "../../hooks/useLiveRef";
import rootSelector from "../rootSelector";
import {clearApi} from "./apiActions";
import {ApiResponseState, ApiState, DEFAULT_SLICE_KEY} from "./apiReducer";
import {initRequest} from "../../middlewares/request/requestActions";

// Global cache for deduplication system
const globalRequestCache = new Map<
  string,
  {
    requestKey: string;
    subscribers: Set<string>;
  }
>();

// Function to generate a unique hash from the request
const generateRequestHash = (payload: Omit<RequestAction["payload"], "label"> & {clearCache?: boolean}): string => {
  // Remove fields that shouldn't affect the cache
  const {uuid, extra, clearCache, ...cacheablePayload} = payload;
  const hash = JSON.stringify(cacheablePayload);
  return hash;
};

const selectApi = <Data = any, Error = any>(label: string) =>
  createSelector(rootSelector, r => r.api[label] as ApiState<Data, Error>);

type RequestAction = ReturnType<typeof initRequest>;
type RequestPayload = Omit<RequestAction["payload"], "label"> & {clearCache?: boolean};

type UseApiOptions<D = any, E = any> = {
  /**
   * if true it immediately dispatches the request action
   * @default false
   */
  invokeImmediately?: boolean;
  /**
   * Callback invoked after the request is successful
   * @param data the successful response data
   * @param headers the successful response headers
   */
  onSuccess?: (data: D, headers: AxiosHeaders) => void;
  /**
   * Callback invoked if the request responds with an error
   * @param error the error response data
   * @returns
   */
  onError?: (error: E, statusCode: number, headers: AxiosHeaders) => void;
  /**
   * When set to true it enables the caching functionalities of the `useApi` hook.
   * If a cache key getter is not declared as an option (`getCacheKey`)
   * every request after the first one will not be dispatched
   * @default false
   */
  cache?: boolean;
  /**
   * function that calculates a (possibly) unique key given a request payload
   * The result of this fucntion is used as the key for a response stored in the state so that,
   * given the same request, the same response will be retrieved without dispatching again the request.
   * @param request
   * @returns the key for a given request
   */
  getCacheKey?: (request: RequestPayload) => string | symbol | number;
  /** clears the state for a request action, including any cached value */
  clearOnUnmount?: boolean;
  /**
   * when set to true it saves any response result under the request action label.
   * By doing so, any time useApi is used with the same action and this flag enabled,
   * the same data will be retrieved. Different cache keys may still be used by defining
   * `getCacheKey` function.
   * @default false
   */
  cacheAction?: boolean;
  /**
   * When set to true, enables request deduplication. Multiple components making
   * the same request will share the same network call.
   * @default false
   */
  enableDeduplication?: boolean;
};

type CallParam = RequestPayload | ((v: RequestPayload) => RequestPayload);

export type UseApiResult<Data = any, Error = any> = {
  data: Data;
  error: Error;
  loading: boolean;
  call: (v?: CallParam) => void;
  request: RequestPayload;
  clear: () => void;
  headers: AxiosHeaders;
  statusCode?: number;
};

const createResponseSubject = <Data = any, Error = any>() => ({
  next(v: ApiResponseState<Data, Error>) {
    this._subscription.next(v);
  },
  error(v: ApiResponseState<Data, Error>) {
    this._subscription.error(v);
  },
  subscribe(cb: {next: (d: ApiResponseState<Data, Error>) => void; error: (e: ApiResponseState<Data, Error>) => void}) {
    this._subscription = cb;
  },
  _subscription: {next: (d: ApiResponseState<Data, Error>) => {}, error: (e: ApiResponseState<Data, Error>) => {}},
  unsubscribe() {
    this._subscription = {next: d => {}, error: e => {}};
  }
});

/** Utilty hook that helps handling a RequestAction by dispatching it with specific parameters and monitors its status.
 * It uses the redux state slice `api` for storing request/response data.
 * It provides useful optional functionalities such as caching and callbacks on success and error result.
 * @param initialRequestAction - an action created by the `initRequest` function. If the action is not immediately dispatched (see option invokeImmediately),
 * all the parameters of the action payload may be added later when dispatching the action by using the returned functions `call` or `setRequest`
 * @param opts set of optional fields
 * */
const useApi = <Data = any, Error = any>(
  initialRequestAction: RequestAction,
  opts: UseApiOptions<Data, Error> = {}
): UseApiResult<Data, Error> => {
  const mergedOptions = {
    invokeImmediately: false,
    getCacheKey: () => DEFAULT_SLICE_KEY,
    enableDeduplication: false,
    ...opts
  };
  const initialRequestActionRef = useRef(initialRequestAction);
  const resposeSubject = useRef(createResponseSubject<Data, Error>());

  // Unique ID for this hook instance (for tracking subscribers)
  const subscriberId = useRef(uuidv4());

  // requestKey can be shared or unique
  const [requestKey, setRequestKey] = useState(() =>
    mergedOptions?.cacheAction ? initialRequestAction.payload.label : uuidv4()
  );

  const renderCountAfterRequestSent = useRef(0);
  const options = useLiveRef(mergedOptions);
  const lastRequestRef = useRef<RequestPayload>(initialRequestAction.payload);
  const [cacheKey, setCacheKey] = useState<string | symbol | number>(DEFAULT_SLICE_KEY);
  const currentCacheSlice = useRef<string | symbol | number>(DEFAULT_SLICE_KEY);
  const dispatch = useDispatch();
  const apiState = useSelector(selectApi<Data, Error>(requestKey));
  const apiStateSlice = apiState?.[cacheKey];
  const firstInvocationDone = useRef(false);
  const currentApiStateRef = useLiveRef(apiState);
  const isUsingCache = options.current?.cache || options.current?.cacheAction;
  const currentRequestHashRef = useRef<string>("");

  const dispatchRequest = useCallback(
    (request: RequestPayload, cacheKey: string | symbol | number, targetRequestKey?: string) => {
      const finalRequestKey = targetRequestKey || requestKey;
      dispatch({
        ...initialRequestActionRef.current,
        payload: {
          uuid: uuidv4(),
          ...request,
          extra: {
            ...(request?.extra || {}),
            cacheKey: isUsingCache ? cacheKey : DEFAULT_SLICE_KEY,
            requestKey: finalRequestKey
          }
        }
      });
    },
    [dispatch, initialRequestActionRef, isUsingCache, requestKey]
  );

  const getCacheKey = useCallback(
    (request: RequestPayload) => options.current?.getCacheKey?.(request) || DEFAULT_SLICE_KEY,
    [options]
  );

  const call = useCallback(
    (newRequest?: CallParam) => {
      const currentRequest =
        (typeof newRequest === "function" ? newRequest(lastRequestRef.current) : newRequest) || lastRequestRef.current;
      const cacheKey = getCacheKey(currentRequest);
      setCacheKey(cacheKey);
      lastRequestRef.current = currentRequest;

      const nextApiStateSlice = currentApiStateRef.current?.[cacheKey];
      if (
        isUsingCache &&
        nextApiStateSlice?.state === "SUCCESS" &&
        !!nextApiStateSlice?.data &&
        !currentRequest?.clearCache
      ) {
        return;
      }

      if (options.current?.enableDeduplication) {
        const requestHash = generateRequestHash(currentRequest);
        currentRequestHashRef.current = requestHash;

        const existingRequest = globalRequestCache.get(requestHash);

        if (existingRequest) {
          setRequestKey(existingRequest.requestKey);
          existingRequest.subscribers.add(subscriberId.current);
          return;
        } else {
          const newRequestKey = mergedOptions?.cacheAction ? initialRequestAction.payload.label : uuidv4();
          setRequestKey(newRequestKey);

          globalRequestCache.set(requestHash, {
            requestKey: newRequestKey,
            subscribers: new Set([subscriberId.current])
          });

          dispatchRequest(currentRequest, cacheKey, newRequestKey);
        }
      } else {
        dispatchRequest(currentRequest, cacheKey);
      }
    },
    [
      currentApiStateRef,
      dispatchRequest,
      getCacheKey,
      isUsingCache,
      options,
      mergedOptions?.cacheAction,
      initialRequestAction.payload.label
    ]
  );

  useEffect(() => {
    if (apiStateSlice?.state === "PENDING" || currentCacheSlice.current !== cacheKey) {
      renderCountAfterRequestSent.current++;
      currentCacheSlice.current = cacheKey;
    }
  }, [apiStateSlice?.state, cacheKey]);

  useEffect(() => {
    if (renderCountAfterRequestSent.current >= 1) {
      if (apiStateSlice?.state === "ERROR") {
        resposeSubject.current.error(apiStateSlice);
        renderCountAfterRequestSent.current = 0;
      }
      if (apiStateSlice?.state === "SUCCESS") {
        resposeSubject.current.next(apiStateSlice);
        renderCountAfterRequestSent.current = 0;
      }
    }
  }, [apiStateSlice]);

  const clear = useCallback(() => {
    for (const [mapKey, cacheEntry] of globalRequestCache) {
      if (cacheEntry.requestKey === requestKey) {
        cacheEntry.subscribers.delete(subscriberId.current);
        if (cacheEntry.subscribers.size === 0) {
          globalRequestCache.delete(mapKey);
        }
        break;
      }
    }
    dispatch(clearApi(requestKey));
  }, [dispatch, requestKey]);

  useEffect(() => {
    if (options.current?.invokeImmediately && !firstInvocationDone.current) {
      call();
      firstInvocationDone.current = true;
    }
  }, [options, call]);

  useEffect(() => {
    const currentOptionsRef = options.current;
    return () => {
      if (currentOptionsRef?.clearOnUnmount) {
        clear();
      }
    };
  }, [clear, options]);

  useEffect(() => {
    const sub = resposeSubject.current;
    if (options.current?.onSuccess || options.current?.onError) {
      sub.subscribe({
        next: ({data, responsePayload}) => options.current?.onSuccess?.(data, responsePayload.extra.responseHeaders),
        error: ({error, responsePayload}) =>
          options.current?.onError?.(error, responsePayload.statusCode, responsePayload.extra.responseHeaders)
      });
    }
    return () => {
      sub.unsubscribe();
    };
  }, [options]);

  const result = {
    data: apiStateSlice?.data,
    error: apiStateSlice?.error,
    loading: apiStateSlice?.loading,
    call,
    get request() {
      return lastRequestRef.current;
    },
    clear,
    headers: apiStateSlice?.responsePayload?.extra?.responseHeaders,
    statusCode: apiStateSlice?.responsePayload?.statusCode
  };
  return result;
};

export default useApi;
